import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { PopupService, PopupData } from '../../../core/services/popup-service/popup.service';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-popup',
  templateUrl: './popup.component.html',
  styleUrls: ['./popup.component.scss']
})
export class PopupComponent implements OnInit, OnDestroy {
  popupData: PopupData | null = null;
  isVisible = false;
  isClosing = false;
  localizedContent = { title: '', buttonText: '' };
  currentLanguage = 'fr';
  imageError = false;
  autoCloseCountdown = 0;

  private popupSubscription: Subscription = new Subscription();
  private routerSubscription: Subscription = new Subscription();
  private autoCloseTimer: any;

  constructor(
    private popupService: PopupService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.currentLanguage = this.popupService.getCurrentLanguage();

    // Subscribe to popup data
    this.popupSubscription = this.popupService.popup$.subscribe((popup) => {
      if (popup) {
        this.popupData = popup;
        this.localizedContent = this.popupService.getLocalizedContent(popup);
        this.imageError = false; // Reset image error state for new popup

        // Check if popup should be shown on current page
        if (this.popupService.shouldShowOnCurrentPage(popup.display_location)) {
          this.showPopup();
        }
      } else {
        this.hidePopup();
      }
    });

    // Listen for route changes to re-check popup
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Re-check popup when navigation completes
      setTimeout(() => {
        this.checkAndShowPopup();
      }, 1000);
    });

    // Initialize popup check with delay (mimicking PHP logic)
    setTimeout(() => {
      this.checkAndShowPopup();
    }, 1500);
  }

  private checkAndShowPopup(): void {
    this.popupService.showPopupIfNeeded();
  }

  ngOnDestroy(): void {
    this.popupSubscription.unsubscribe();
    this.routerSubscription.unsubscribe();
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
    }
  }

  showPopup(): void {
    if (this.isVisible) return;

    // Prevent body scrolling
    document.body.style.overflow = 'hidden';
    this.isVisible = true;

    // Add show class after a brief delay for animation
    setTimeout(() => {
      const overlay = document.querySelector('.popup-overlay');
      if (overlay) {
        overlay.classList.add('show');
      }
    }, 50);

    // Auto-close timer based on popup data
    if (this.popupData?.auto_close && this.popupData.auto_close > 0) {
      this.autoCloseCountdown = this.popupData.auto_close;
      this.startAutoCloseCountdown();
    }
  }

  hidePopup(): void {
    if (!this.isVisible) return;

    // Clear auto-close timer if exists
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }

    this.isClosing = true;

    const overlay = document.querySelector('.popup-overlay');
    if (overlay) {
      overlay.classList.add('closing');
      overlay.classList.remove('show');
    }

    setTimeout(() => {
      this.isVisible = false;
      this.isClosing = false;
      this.popupData = null;
      document.body.style.overflow = '';

      if (overlay) {
        overlay.classList.remove('closing');
      }
    }, 400);
  }

  closePopup(): void {
    this.popupService.hidePopup();
  }

  onDocumentButtonClick(): void {
    if (this.popupData?.document_url) {
      // Open document in new tab
      window.open(this.popupData.document_url, '_blank');

      // Close popup after a brief delay
      setTimeout(() => {
        this.closePopup();
      }, 1000);
    }
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.closePopup();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapePress(event: KeyboardEvent): void {
    if (this.isVisible) {
      this.closePopup();
    }
  }

  get isRTL(): boolean {
    return this.currentLanguage === 'ar';
  }

  get hasTitle(): boolean {
    return !!this.localizedContent.title;
  }

  onImageError(): void {
    console.warn('Failed to load popup image:', this.popupData?.image_url);
    this.imageError = true;
  }

  onImageLoad(): void {
    this.imageError = false;
  }

  /**
   * Start auto-close countdown with visual feedback
   */
  private startAutoCloseCountdown(): void {
    const countdownInterval = setInterval(() => {
      this.autoCloseCountdown--;

      if (this.autoCloseCountdown <= 0) {
        clearInterval(countdownInterval);
        this.closePopup();
      }
    }, 1000);

    // Store interval reference in autoCloseTimer for cleanup
    this.autoCloseTimer = countdownInterval;
  }

  /**
   * Get auto-close display text
   */
  get autoCloseText(): string {
    if (this.autoCloseCountdown > 0) {
      return this.isRTL
        ? `سيتم الإغلاق خلال ${this.autoCloseCountdown} ثانية`
        : `Fermeture dans ${this.autoCloseCountdown}s`;
    }
    return '';
  }

}